import { createModulePermissionMethods } from "../../admin-shared/modulePermissions";
import { isOrganizationAdminScope } from "../../admin-shared/adminScopeStorage";
import { orgEntitlementAccessMixin } from "../../admin-shared/orgEntitlementAccess";

const parseJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const resolveRole = () => {
  const stored = (localStorage.getItem("naturals_workspace_role") || "").toLowerCase();
  if (stored === "super_admin" || stored === "admin" || stored === "user") return stored;
  return "user";
};

/** Keys that previously lived under edit_set_flow — inherit when absent. */
const SET_FLOW_CHILDREN = new Set([
  "manage_kiosk_landings",
  "manage_skin_settings",
]);

const MODULE_KEY = "naturals_workspace_accessModules";

let _moduleGate = null;
function moduleGate() {
  if (!_moduleGate) {
    _moduleGate = createModulePermissionMethods(MODULE_KEY, {
      isSuperAdmin: () => permissionService.isSuperAdmin(),
      isAdmin: () => permissionService.isAdmin(),
      isFranchise: () => permissionService.isFranchise(),
    });
  }
  return _moduleGate;
}

export const permissionService = {
  getRole() {
    const role = resolveRole();
    localStorage.setItem("naturals_workspace_role", role);
    return role;
  },

  getAllowedModules() {
    return moduleGate().getAllowedModules();
  },

  hasAny(...args) {
    return moduleGate().hasAny(...args);
  },

  hasAll(...args) {
    return moduleGate().hasAll(...args);
  },

  has(moduleId) {
    return moduleGate().has(moduleId);
  },

  /** HQ Admin or Super Admin (shared admin URL). */
  isAdmin() {
    const role = this.getRole();
    return role === "admin" || role === "super_admin";
  },

  isSuperAdmin() {
    return this.getRole() === "super_admin";
  },

  getAdminScope() {
    return isOrganizationAdminScope("naturals_workspace") ? "organization" : "regions";
  },

  isOrganizationScope() {
    if (this.isSuperAdmin()) return true;
    return this.getRole() === "admin" && this.getAdminScope() === "organization";
  },

  canManageHqAdmins() {
    if (this.isSuperAdmin()) return true;
    if (!this.isOrganizationScope()) return false;
    return this.getAdminPermission("manage_hq_admins", false);
  },

  /** Franchise store login (separate URL). */
  isFranchise() {
    return this.getRole() === "user";
  },

  /** Only Admin (not Super Admin) creates franchise users. */
  canCreateFranchiseUsers() {
    if (this.getRole() !== "admin") return false;
    return this.getAdminPermission("create_franchise_users", true);
  },

  getAdminPermissions() {
    try {
      const raw = localStorage.getItem("naturals_workspace_admin_permissions");
      if (!raw) return {};
      const perms = JSON.parse(raw);
      return perms && typeof perms === "object" ? perms : {};
    } catch {
      return {};
    }
  },

  getAdminPermission(key, defaultValue = false) {
    if (this.isSuperAdmin()) return true;
    const perms = this.getAdminPermissions();
    if (typeof perms[key] === "boolean") return perms[key];
    // Legacy HQ admins only had edit_set_flow for page/kiosk/terms/skin.
    if (SET_FLOW_CHILDREN.has(key) && typeof perms.edit_set_flow === "boolean") {
      return perms.edit_set_flow;
    }
    // Permissions never loaded — preserve legacy admin access using caller default.
    const raw = localStorage.getItem("naturals_workspace_admin_permissions");
    if (raw === null && this.isAdmin()) return defaultValue;
    return false;
  },

  canUseWhatsappCredentials() {
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("whatsapp_credentials", false);
  },

  canEditSetFlow() {
    if (this.isFranchise()) return true;
    if (this.isSuperAdmin()) return false;
    if (!this.isOrganizationScope()) return false;
    return (
      this.getAdminPermission("edit_org_page_setup", false) ||
      this.getAdminPermission("edit_set_flow", false)
    );
  },

  canManageKioskLandings() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return false;
    if (!this.isOrganizationScope()) return false;
    return this.getAdminPermission("manage_kiosk_landings", false);
  },

  canManageTerms() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("manage_terms", true);
  },

  canManageSkinSettings() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return false;
    if (!this.isOrganizationScope()) return false;
    return (
      this.getAdminPermission("manage_org_skin_settings", false) ||
      this.getAdminPermission("manage_skin_settings", false)
    );
  },

  canManageAiChat() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("manage_ai_chat", true);
  },

  canResolveComplaints() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return true;
    return this.getAdminPermission("resolve_complaints", true);
  },

  canViewComplaints() {
    if (this.isFranchise()) return true;
    if (this.isSuperAdmin()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_complaints", true);
  },

  canManageProducts() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return false;
    if (!this.isOrganizationScope()) return false;
    return (
      this.getAdminPermission("manage_org_products", false) ||
      this.getAdminPermission("manage_products", false)
    );
  },

  canManageServices() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return false;
    if (!this.isOrganizationScope()) return false;
    return (
      this.getAdminPermission("manage_org_services", false) ||
      this.getAdminPermission("manage_services", false)
    );
  },

  canVerifyCatalog() {
    if (this.isSuperAdmin()) return false;
    if (!this.isOrganizationScope()) return false;
    return this.getAdminPermission("verify_catalog", false);
  },

  canViewOrgServicesReadonly() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_org_services_readonly", true);
  },

  canViewDashboard() {
    if (this.isFranchise()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_dashboard", true);
  },

  canViewStoreKpis() {
    if (this.isFranchise()) return true;
    if (this.isSuperAdmin()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_store_kpis", true);
  },

  canViewContactMessages() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_contact_messages", true);
  },

  canEditCompanyProfile() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return true;
    if (!this.isOrganizationScope()) return false;
    return this.getAdminPermission("company_profile", false);
  },

  getPlan() {
    return (localStorage.getItem("naturals_workspace_plan") || "").toLowerCase();
  },

  getPlanStatus() {
    return (localStorage.getItem("naturals_workspace_plan_status") || "active").toLowerCase();
  },

  getDisplayPlanLabel() {
    const plan = this.getPlan();
    const status = this.getPlanStatus();
    const formattedPlan = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "Free";
    if (status === "trial_active") return `Trial - ${formattedPlan}`;
    return `${formattedPlan} Plan`;
  },

  ...orgEntitlementAccessMixin("workspace"),
};
